import React, { useMemo, useState } from "react";
import { Box, Paper, Typography, CircularProgress, Alert, Button, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import EnhancedTable from "../../../components/common/EnhancedTable";
import { useStudentAccounts, StudentAccountsResponseDto } from "../../../hooks/useStudentAccounts";
import { useError } from "../../../contexts/ErrorContext";
import FeePaymentModal from "../../../components/Modals/FeePaymentModal";
import CreateAccountModal from "../../../components/Modals/CreateAccountModal";
import EditAccountModal from "../../../components/Modals/EditAccountModal";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";

const StudentAccountsManagement: React.FC = () => {
    const { selectedSession, selectedClass } = useSessionClass();
    const { t } = useTranslation();
    const { showError } = useError();

    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [createAccountModalOpen, setCreateAccountModalOpen] = useState(false);
    const [editAccountModalOpen, setEditAccountModalOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<StudentAccountsResponseDto | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const { data, isLoading, error } = useStudentAccounts(
        selectedClass?.id,
        selectedSession?.id
    );

    const handlePay = (account: StudentAccountsResponseDto) => {
        setSelectedAccount(account);
        setPaymentModalOpen(true);
    };

    const handleEdit = (account: StudentAccountsResponseDto) => {
        setSelectedAccount(account);
        setEditAccountModalOpen(true);
    };

    const tableData = useMemo(() => {
        if (!data) return [];
        return data
            .map((account) => ({
                ...account,
                studentName: account.student?.studentName || "N/A",
                studentCode: account.student?.studentCode || "N/A",
                payButton: account,
                editButton: account,
            }))
            .sort((a, b) => a.studentName.localeCompare(b.studentName));
    }, [data]);

    const columns = [
        {
            id: "studentName",
            label: t("student_name"),
            minWidth: 200
        },
        {
            id: "studentAccountNumber",
            label: t("account_number"),
            minWidth: 200
        },
        {
            id: "dueAmount",
            label: t("due_amount"),
            minWidth: 120,
            align: "right" as const,
            format: (value: number) => `₹${value?.toLocaleString() || "0"}`
        },
        {
            id: "editButton",
            label: t("edit") || "Edit",
            minWidth: 80,
            align: "center" as const,
            format: (account: any) => (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => handleEdit(account)}
                    sx={{
                        borderColor: "#3092e3",
                        color: "#3092e3",
                        "&:hover": { borderColor: "#1976d2", color: "#1976d2", backgroundColor: "#e8f4fd" },
                        textTransform: "none",
                        borderRadius: 2,
                    }}
                >
                    {t("edit") || "Edit"}
                </Button>
            )
        },
        {
            id: "payButton",
            label: t("pay"),
            minWidth: 100,
            align: "center" as const,
            format: (account: any) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => handlePay(account)}
                    sx={{
                        backgroundColor: "#3092e3",
                        "&:hover": { backgroundColor: "#1976d2" },
                        textTransform: "none",
                        borderRadius: 2,
                    }}
                >
                    {t("pay")}
                </Button>
            )
        },
    ];

    const isActuallyLoading = isLoading && !!selectedClass?.id && !!selectedSession?.id;

    if (isActuallyLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{t("error_loading_accounts")}</Alert>
            </Box>
        );
    }

    return (
        <>
            <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
                        {t("student_accounts")}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TextField
                            size="small"
                            placeholder={t("search")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
                        />
                        {selectedSession && selectedClass && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<PersonAddIcon />}
                                onClick={() => setCreateAccountModalOpen(true)}
                                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                            >
                                {t("create_account") || "Create Account"}
                            </Button>
                        )}
                    </Box>
                </Box>

                {(!selectedSession || !selectedClass) && (
                    <Alert severity="info">
                        {t("please_select_session_and_class_for_accounts") || "Please select a session and class"}
                    </Alert>
                )}

                <EnhancedTable
                    columns={columns}
                    data={tableData}
                    searchFields={["studentName", "studentAccountNumber"]}
                    hideSearch
                    externalSearchTerm={searchTerm}
                    cardProps={{
                        elevation: 0,
                        sx: {
                            border: "none",
                            boxShadow: "none",
                            backgroundColor: "transparent",
                            mb: 0,
                            '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
                        },
                    }}
                />
            </Paper>

            <FeePaymentModal
                open={paymentModalOpen}
                handleClose={() => setPaymentModalOpen(false)}
                account={selectedAccount}
            />

            <EditAccountModal
                open={editAccountModalOpen}
                handleClose={() => setEditAccountModalOpen(false)}
                account={selectedAccount}
            />

            {selectedClass && selectedSession && (
                <CreateAccountModal
                    open={createAccountModalOpen}
                    handleClose={() => setCreateAccountModalOpen(false)}
                    classId={selectedClass.id!}
                    sessionId={selectedSession.id!}
                />
            )}
        </>
    );
};

export default StudentAccountsManagement;
